import { NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Self } from "@angular/core";
import { IAdaptMenuItem } from "@common/ux/menu/menu.component";
import { ResponsiveService } from "@common/ux/responsive/responsive.service";
import isEqual from "lodash.isequal";
import { merge, timer } from "rxjs";
import { switchMap, take } from "rxjs/operators";
import { ColumnSize, ComponentSize, ComponentSizePresetGroups, ComponentSizeWeights, ILayoutColumn, ILayoutComponent } from "../layout.interface";
import { LayoutService } from "../layout.service";
import { LayoutBaseComponent } from "../layout-base.component";
import { LayoutManagerComponent } from "../layout-manager/layout-manager.component";

const DefaultWidthPreset = "Default Width";

@Component({
    selector: "adapt-layout-component",
    templateUrl: "./layout-component.component.html",
    styleUrls: ["./layout-component.component.scss"],
    providers: [NgClass],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class LayoutComponentComponent extends LayoutBaseComponent<ILayoutComponent> implements OnInit, OnChanges, OnDestroy {

    @Input("id") public componentId!: string;

    @Input() public defaultUserHidden?: boolean;
    @Input() public displayName?: string;

    public layoutComponent!: ILayoutComponent;
    public layoutColumn!: ILayoutColumn;
    public menuItems!: IAdaptMenuItem[];

    constructor(
        @Self() protected ngClass: NgClass,
        protected elementRef: ElementRef<HTMLElement>,
        protected layoutManager: LayoutManagerComponent,
        protected responsiveService: ResponsiveService,
    ) {
        super(ngClass, elementRef, layoutManager, responsiveService);

        merge(
            this.layoutManager.initialised,
            this.layoutManager.layoutUpdated$,
        ).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            const {
                component,
                column,
            } = LayoutService.findComponentInLayout(this.layoutManager.layout.columns, this.componentId);
            if (component && column) {
                this.layoutComponent = component;
                this.layoutColumn = column;
            }

            this.ngOnChanges();
        });
    }

    public get layoutEntity() {
        return this.layoutComponent;
    }

    public get shouldProjectContent() {
        // component is not enabled at all, never project content
        if (!this.layoutEntity?.options?.enabled) {
            return false;
        }

        // if using the hidden component menu, only project the content when the component is added
        // otherwise, if not in edit mode, only show components that aren't hidden.
        if (this.layoutManager.useHiddenComponentMenu || !this.layoutManager.isEditing) {
            return this.layoutEntity?.options?.userHidden !== true;
        }

        // at this point they're in edit mode, so always project content
        return true;
    }

    private get visibleComponents() {
        if (this.layoutColumn) {
            return this.layoutColumn.options.components.filter((cmp) => (cmp.options?.enabled ?? true)
                && (!this.layoutManager.useHiddenComponentMenu || cmp.options?.userHidden !== true));
        }

        return [];
    }

    public get componentHidden() {
        return this.layoutEntity?.options?.userHidden ?? false;
    }

    public get isFirstComponentInColumn() {
        if (this.layoutEntity && this.layoutColumn) {
            return this.visibleComponents.indexOf(this.layoutEntity) === 0;
        }
        return false;
    }

    public get isLastComponentInColumn() {
        if (this.layoutEntity && this.layoutColumn) {
            const visibleComponents = this.visibleComponents;
            return visibleComponents.indexOf(this.layoutEntity) === visibleComponents.length - 1;
        }
        return false;
    }

    public get inSmallColumn() {
        // couldn't find a matching column at this point (probably not initialised fully at this moment...)
        if (!this.layoutColumn) {
            return false;
        }

        const columnSizePresets = this.layoutManager.columnSizePresets;

        const columnIdx = this.layoutManager.layout.columns.indexOf(this.layoutColumn);
        const colSizePreset = Object.keys(columnSizePresets).find((colPreset: ColumnSize) =>
            isEqual(this.layoutColumn.size, columnSizePresets[colPreset]?.[columnIdx])) as ColumnSize | undefined;

        // couldn't find a matching preset
        if (!colSizePreset) {
            return false;
        }

        const preset = columnSizePresets[colSizePreset];

        // get the correct small column index for the given column size type
        const columnIdxMapping: Partial<Record<ColumnSize, number>> = {
            [ColumnSize.NarrowLeftHand]: 0,
            [ColumnSize.NarrowLeftHandThird]: 0,
            [ColumnSize.NarrowRightHand]: 1,
            [ColumnSize.NarrowRightHandThird]: 1,
        };

        // if column is not one of the narrow presets we are not in a small column
        if (!Object.keys(columnIdxMapping).includes(colSizePreset)) {
            return false;
        }

        return preset?.[columnIdxMapping[colSizePreset]!] === this.layoutColumn.size;
    }

    public ngOnInit() {
        this.layoutComponent = this.layoutManager.getComponent(
            this.componentId,
            this,
            this.elementRef.nativeElement,
            this.defaultSize,
        );

        this.elementRef.nativeElement.id = this.layoutEntity.id;

        this.ngOnChanges();
    }

    public ngOnDestroy() {
        super.ngOnDestroy();

        if (this.layoutEntity) {
            this.layoutManager.removeComponent(this.layoutEntity);
        }
    }

    public ngOnChanges() {
        // only set default userHidden if there isn't already a value
        if (this.layoutEntity && this.defaultUserHidden !== undefined && this.layoutEntity.options?.userHidden === undefined) {
            LayoutService.setOptions<ILayoutComponent>(this.layoutEntity, { userHidden: this.defaultUserHidden });
        }

        if (this.layoutEntity && this.displayName !== undefined) {
            LayoutService.setOptions<ILayoutComponent>(this.layoutEntity, { displayName: this.displayName });
        }

        this.updateMenu();
        super.ngOnChanges();
    }

    public moveComponent(change: number) {
        if (this.layoutEntity && this.layoutColumn) {
            const components = this.layoutColumn.options.components;
            let desiredIndex = components.indexOf(this.layoutEntity) + change;

            const rows = LayoutService.getColumnRows(components);
            const currentRow = rows.findIndex((row) => row.includes(this.layoutEntity));
            const desiredRow = currentRow + change;

            if (desiredRow < 0 || desiredRow >= rows.length) {
                // move within row since can't move out of the row
                while (components[desiredIndex].options?.enabled === false || (this.layoutManager.useHiddenComponentMenu && components[desiredIndex].options?.userHidden === true)) {
                    desiredIndex += change;

                    if (desiredIndex < 0) {
                        desiredIndex = 0;
                        break;
                    } else if (desiredIndex > components.length - 1) {
                        desiredIndex = components.length - 1;
                        break;
                    }
                }
            } else {
                const isFullWidth = rows[currentRow].length === 1;
                const rowComponents = rows[desiredRow];

                let targetComponent = rowComponents[0];
                if (isFullWidth && rowComponents.length !== 1 && change > 0) {
                    // this component only fit in a single row, and the desired row has multiple components, so move it a full row...
                    // we only need this logic if moving downwards. inserts after the last component in the desired row
                    targetComponent = rowComponents[rowComponents.length - 1];
                }

                desiredIndex = components.findIndex((cmp) => cmp === targetComponent);
            }

            this.layoutManager.moveComponent(this.layoutEntity, this.layoutColumn, this.layoutColumn, desiredIndex);

            // move to component once the layout has updated
            this.layoutManager.layoutUpdated$.pipe(
                take(1),
                switchMap(() => timer(100)),
            ).subscribe(() => {
                this.elementRef.nativeElement.scrollIntoView({ behavior: "smooth" });
            });

            this.layoutManager.updateLayout();
        }
    }

    public toggleHideComponent() {
        LayoutService.setOptions(this.layoutEntity, { userHidden: !this.componentHidden || undefined });
        this.layoutManager.updateLayout();
    }

    private updateMenu() {
        const inSmallColumn = this.inSmallColumn;

        const items: IAdaptMenuItem[] = [];

        const generateMenuItem = (text: string, sizes: string[]) => {
            return {
                text,
                icon: "",
                onClick: () => this.updateSize(sizes[0]),
                isChecked: sizes.includes(this.layoutEntity?.size ?? ""),
                // disable if column too small for component size
                disabled: (ComponentSizeWeights[text as ComponentSize] ?? 1.0) < 0.5 && inSmallColumn,
            } as IAdaptMenuItem;
        };

        const componentSizePresets = this.layoutManager.componentSizePresets;

        // no matching size, add the stored default as a preset
        if (this.defaultSize && !Object.values(componentSizePresets).some((sizes) => sizes.includes(this.defaultSize!))) {
            items.push(generateMenuItem(DefaultWidthPreset, [this.defaultSize]));
        }

        for (const [text, componentSizes] of Object.entries(ComponentSizePresetGroups)) {
            // ungrouped, no children
            if (text === "") {
                for (const componentSize of componentSizes) {
                    const preset = componentSizePresets[componentSize];
                    if (preset) {
                        items.push(generateMenuItem(componentSize, preset));
                    }
                }
            } else {
                if (componentSizes.length > 0) {
                    items.push({
                        text,
                        icon: "",
                        disabled: true,
                        separatorTop: true,
                    });

                    for (const componentSize of componentSizes) {
                    const preset = componentSizePresets[componentSize];
                        if (preset) {
                            items.push(generateMenuItem(componentSize, preset));
                        }
                    }
                }
            }
        }

        if (items.length > 1) {
            this.menuItems = [
                {
                    text: "",
                    icon: "fal fa-fw fa-columns",
                    items,
                },
            ];
        } else {
            // only one size option, hide the menu
            this.menuItems = [];
        }
    }

    private updateSize(size?: string) {
        LayoutService.setSize(this.layoutEntity, size);
        this.layoutManager.updateLayout();
    }
}
